import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { Player } from '@lottiefiles/react-lottie-player';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { useTelemetryViewEvent } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';
import { merchantSelfServiceSignUpView } from 'core/telemetry/avo/Avo';
import { useRegisterTelemetryEvent } from 'core/telemetry/hooks/useRegisterTelemetryEvent';
import MerchantTyping from 'lottie/merchant_typing.json';

import SelfServeSignUpForm from './SelfServeSignUpForm/SelfServeSignUpForm';
import { useCookieConsents } from '../../cookie-consents/CookieConsentsProvider';

type Props = { onSuccess: () => void };

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  margin-block-start: 48px;
  max-width: 650px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline: 16px;
    margin-block-start: 24px;
  }
`;

const Heading = styled(T.Heading2)`
  text-align: center;
  margin-block-start: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-block-start: 24px;
    font-size: 36px;
  }
`;

const Subheading = styled(T.Body3)`
  margin-block-start: 8px;
  margin-block-end: 56px;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-block-end: 24px;
  }
`;

const SelfServeFormView = ({ onSuccess }: Props) => {
  useTelemetryViewEvent('merchantSelfServiceSignUpView', {}, durationMs => {
    merchantSelfServiceSignUpView({ durationMs });
  });
  const [woltUid] = useCookie(getCookieName('wolt-uid'));

  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();

  const register = useRegisterTelemetryEvent();

  useEffect(() => {
    register({
      event: 'form_viewed',
      marketingConsent,
      analyticsConsent,
      properties: {
        __woltUid: woltUid,
      },
    });
  }, [analyticsConsent, marketingConsent, register, woltUid]);

  return (
    <Wrapper>
      <Player autoplay loop src={MerchantTyping} style={{ width: '300px' }}></Player>

      <Heading>
        <FormattedMessage id="self-serve-sign-up.header-text" />
      </Heading>

      <Subheading>
        <FormattedMessage id="self-serve-sign-up.subheading-text.we-are-excited" />
      </Subheading>

      <SelfServeSignUpForm onSuccess={onSuccess} />
    </Wrapper>
  );
};

export default SelfServeFormView;
